<template>
  <div :class="{ 'calendar-navigation': true, 'flight-details-is-open': !!selectedFlight }">
    <div class="calendar-navigation-ui">
      <div class="calendar-navigation-ui-date">
        <d-button
          size="sm"
          outline
          @click.prevent="dateToday">
          Today
        </d-button>

        <d-button
          @click.prevent="datePrev"
          outline
          size="sm"
          class="ml-2">
          <i class="material-icons">
            keyboard_arrow_left
          </i>
        </d-button>

        <d-datepicker
          v-model="selectedDate"
          format="dMMMyy" />

        <d-button
          @click.prevent="dateNext"
          outline
          size="sm">
          <i class="material-icons">
            keyboard_arrow_right
          </i>
        </d-button>
      </div>

      <div class="calendar-navigation-ui-refresh ml-auto mr-auto">
        <d-button
          @click.prevent="refreshCalendar"
          outline
          size="sm">
          <i class="material-icons mr-2">cached</i>
          Refresh
        </d-button>
      </div>

      <div class="mt-1">
        <d-checkbox
          inline
          v-model="showIcao"
          toggle
          toggle-small>
          <small>
            Always show ICAO
          </small>
        </d-checkbox>
      </div>

      <div class="mt-1 ml-2">
        <d-checkbox
          inline
          v-model="showNotes"
          toggle
          toggle-small>
          <small>
            Show notes ({{ notes.length }})
          </small>
        </d-checkbox>
      </div>

      <div class="calendar-navigation-ui-zoom">
        <d-button
          @click.prevent="zoomIn"
          outline
          size="sm"
          :disabled="zoom === '1'">
          <i class="material-icons">add</i>
        </d-button>

        <div class="calendar-navigation-ui-zoom-label">
          {{ currentZoomLevel }}
        </div>

        <d-button
          @click.prevent="zoomOut"
          outline
          size="sm"
          :disabled="zoom === '60'">
          <i class="material-icons">remove</i>
        </d-button>
      </div>
    </div>

    <div
      class="calendar-navigation-axis-x"
      :data-zoom-level="zoom"
      :data-days="daysCount">
      <span
        v-for="(label, index) of axisXLabels"
        :key="'calendar-navigation-axis-x-label-' + index"
        class="calendar-navigation-axis-x-label">
        {{ label }}
      </span>
    </div>

    <div
      v-if="showNotes"
      class="calendar-navigation-notes-area"
      @contextmenu="addNote">
      <template v-for="(notesGroup, index) of groupedNotes">
        <calendar-note
          :key="'grouped-notes-' + index"
          :data-date="notesGroup.date"
          :dateFrom="dateFrom"
          :dateTo="dateTo"
          :noteData="notesGroup"
          :zoom="zoom" />
      </template>
    </div>
  </div>
</template>

<script>
import CalendarHelpers from './CalendarHelpers.js';
import CalendarNote from './CalendarNote.vue';

export default {
  name: 'calendar-navigation',
  components: {
    'calendar-note': CalendarNote
  },
  props: [
    'dateFrom',
    'dateTo',
    'alwaysShowIcao',
    'currentDate',
    'notes',
    'notesLoaded',
    'notesVisible',
    'zoom',
    'zoomLevels'
  ],
  computed: {
    daysCount () {
      return CalendarHelpers.getDaysInPeriod(new Date(this.currentDate), this.zoom);
    },
    axisXLabels () {
      if (this.zoom === '1') {
        return ['3:00', '6:00', '9:00', '12:00', '15:00', '18:00', '21:00'];
      }

      if (this.zoom === '2') {
        return ['6:00', '12:00', '18:00', this.getLabel(1), '6:00', '12:00', '18:00'];
      }

      if (this.zoom === '4') {
        return ['12:00', this.getLabel(1), '12:00', this.getLabel(2), '12:00', this.getLabel(3), '12:00'];
      }

      if (this.zoom === '7') {
        return [
          this.getLabel(1),
          this.getLabel(2),
          this.getLabel(3),
          this.getLabel(4),
          this.getLabel(5),
          this.getLabel(6)
        ];
      }

      if (this.zoom === '14') {
        return [
          this.getLabel(2),
          this.getLabel(4),
          this.getLabel(6),
          this.getLabel(8),
          this.getLabel(10),
          this.getLabel(12)
        ];
      }

      if (this.zoom === '30') {
        return [
          this.getLabel(4),
          this.getLabel(8),
          this.getLabel(12),
          this.getLabel(16),
          this.getLabel(20),
          this.getLabel(24),
          this.getLabel(28)
        ];
      }

      if (this.zoom === '60') {
        return [
          this.getLabel(8),
          this.getLabel(16),
          this.getLabel(24),
          this.getLabel(32),
          this.getLabel(40),
          this.getLabel(48),
          this.getLabel(56)
        ];
      }

      return [];
    },
    currentZoomLevel () {
      return this.zoomLevels[this.zoom];
    },
    groupedNotes () {
      if (!this.notesLoaded) {
        return [];
      }

      return this.notes.reduce((groupedNotes, note) => {
        let items = groupedNotes.find(entry => entry.date === note.date);

        if (!items) {
          items = { date: note.date, notes: [] };
          groupedNotes.push(items);
        }

        items.notes.push(note);
        return groupedNotes;
      }, []);
    }
  },
  watch: {
    selectedDate (newValue) {
      this.$bus.$emit('calendar-set-date', +newValue);
    },
    zoom (newValue) {
      if (newValue === '30' || newValue === '60') {
        let date = new Date();
        date.setTime(this.currentDate);
        date.setDate(1);
        this.selectedDate = date.getTime();
      }
    },
    showIcao (newValue) {
      this.$bus.$emit('calendar-always-show-icao-change', !!newValue);
    },
    showNotes (newValue) {
      this.$bus.$emit('calendar-toggle-notes-visibility', newValue);
    }
  },
  data () {
    return {
      selectedDate: this.currentDate,
      selectedFlight: 0,
      showIcao: false,
      showNotes: false
    };
  },
  mounted () {
    this.showIcao = this.alwaysShowIcao;
    this.showNotes = this.notesVisible;
    this.$bus.$on('refresh-calendar', this.refreshCalendar);
    this.$bus.$on('flight-details-display', this.setSelectedFlight);
    this.$bus.$on('flight-details-close', this.resetSelectedFlight);
    window.addEventListener('resize', this.handleResize);
  },
  methods: {
    zoomIn () {
      this.$bus.$emit('calendar-zoom-in');
    },
    zoomOut () {
      this.$bus.$emit('calendar-zoom-out');
    },
    dateToday () {
      this.selectedDate = +new Date();
    },
    calculateOffset (direction) {
      direction = direction === 'prev' ? -1 : 1;

      switch (this.zoom) {
        case '1':
        case '2':
        case '4':
        case '7':
          return direction * 1 * 24 * 60 * 60 * 1000;
        case '14':
          return direction * 7 * 24 * 60 * 60 * 1000;
        case '30':
          return direction * 1;
        case '60':
          return direction * 1;
        default:
          return 0;
      }
    },
    datePrev () {
      let offset = this.calculateOffset('prev');
      this.setDate(offset);
    },
    dateNext () {
      let offset = this.calculateOffset('next');
      this.setDate(offset);
    },
    setDate (offset) {
      if (Math.abs(offset) < 1000) {
        let date = new Date();
        date.setTime(this.currentDate);
        date.setMonth(date.getMonth() + offset);
        this.selectedDate = date;
      } else {
        let date = new Date().setTime(this.currentDate + offset);
        this.selectedDate = date;
      }
    },
    getLabel (offset = 0) {
      let date = new Date();
      date.setTime(this.currentDate);

      if ([2, 4, 7, 14].indexOf(this.zoom)) {
        date.setDate(date.getDate() + offset);

        let options = {
          month: 'long',
          day: 'numeric'
        };

        return new Intl.DateTimeFormat('en-GB', options).format(date);
      }

      return '';
    },
    refreshCalendar () {
      this.$bus.$once('calendar-flights-updated', () => {
        this.$bus.$emit('calendar-aircrafts-update');
      });
      this.$bus.$emit('calendar-flights-update');
      this.$bus.$emit('calendar-warnings-update');
    },
    setSelectedFlight (id) {
      this.selectedFlight = parseInt(id, 10);
    },
    resetSelectedFlight () {
      this.selectedFlight = 0;
    },
    addNote (event) {
      event.preventDefault();
      let areaWidth = document.querySelector('.calendar-navigation-notes-area').offsetWidth;
      let newNoteDate;

      if (event.target.getAttribute('data-date')) {
        newNoteDate = event.target.getAttribute('data-date');
      } else {
        newNoteDate = this.getDateForClick(event.offsetX, areaWidth);
      }

      this.$bus.$emit('calendar-note-popup-show', {
        date: newNoteDate
      });
    },
    getDateForClick(clickX, divWidth) {
      let timeTo = new Date(this.dateTo.split(' ')[0] + ' 23:59:59 UTC');
      timeTo.setDate(timeTo.getDate() - 1);
      timeTo = timeTo.getTime();
      let timeFrom = new Date(this.dateFrom.split(' ')[0] + ' 00:00:00 UTC').getTime();
      let timeRange = timeTo - timeFrom;
      let fraction = clickX / divWidth;
      let clickTime = timeFrom + (timeRange * fraction);
      let clickDate = new Date(clickTime);
      let year = clickDate.getFullYear();
      let month = (clickDate.getMonth() + 1).toString().padStart(2, '0');
      let day = (clickDate.getDate()).toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    handleResize () {
      this.$bus.$emit('window-size-changed', window.innerWidth);
    }
  },
  beforeDestroy () {
    this.$bus.$off('refresh-calendar', this.refreshCalendar);
    this.$bus.$off('flight-details-display', this.setSelectedFlight);
    this.$bus.$off('flight-details-close', this.resetSelectedFlight);
    window.removeEventListener('resize', this.handleResize);
  }
};
</script>

<style lang="scss" scoped>
.calendar-navigation {
  left: 200px;
  height: 90px;
  position: fixed;
  top: 49px;
  transition: width .25s ease-out;
  width: calc(100% - 200px);
  z-index: 10;

  &.flight-details-is-open {
    width: calc(100% - 600px);
  }

  .btn {
    position: relative;
    top: -1px;

    &[disabled] {
      opacity: .25;
      pointer-events: none;
    }

    i {
      transform: translateY(-2px) scale(1.5);
    }
  }

  .vdp-datepicker {
    margin: -1px 10px 0 10px;

    /deep/ .form-control {
      border-color: #ccc;
      padding: 4px 6px;
      text-align: center;
    }
  }

  &-ui {
    background: #fff;
    border-bottom: 1px solid #eee;
    padding: 10px 20px;
    display: flex;
    height: 50px;

    &-zoom {
      display: flex;
      margin-left: auto;

      .btn {
        i {
          transform: translateY(-2px) scale(1.5);
        }
      }

      &-label {
        line-height: 26px;
        text-align: center;
        width: 100px;
      }
    }

    &-date {
      display: flex;
    }
  }

  &-notes-area {
    background: rgba(0, 123, 255, .125);
    height: 48px;
    margin-top: -5px;
    position: relative;
  }

  &-axis-x {
    height: 40px;
    position: relative;

    &:before {
      background: #fff;
      border-bottom: 1px solid #eee;
      bottom: 4px;
      content: "";
      height: 35px;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
      z-index: 1;
    }

    &-label {
      color: #444;
      font-size: 12px;
      position: absolute;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);
      z-index: 1;
    }

    &[data-zoom-level="1"],
    &[data-zoom-level="2"],
    &[data-zoom-level="4"] {
      .calendar-navigation-axis-x-label:nth-child(1) { left: 12.5%; }
      .calendar-navigation-axis-x-label:nth-child(2) { left: 25%; }
      .calendar-navigation-axis-x-label:nth-child(3) { left: 37.5%; }
      .calendar-navigation-axis-x-label:nth-child(4) { left: 50%; }
      .calendar-navigation-axis-x-label:nth-child(5) { left: 62.5%; }
      .calendar-navigation-axis-x-label:nth-child(6) { left: 75%; }
      .calendar-navigation-axis-x-label:nth-child(7) { left: 87.5%; }
    }

    &[data-zoom-level="7"],
    &[data-zoom-level="14"] {
      .calendar-navigation-axis-x-label:nth-child(1) { left: calc((100% / 7) * 1); }
      .calendar-navigation-axis-x-label:nth-child(2) { left: calc((100% / 7) * 2); }
      .calendar-navigation-axis-x-label:nth-child(3) { left: calc((100% / 7) * 3); }
      .calendar-navigation-axis-x-label:nth-child(4) { left: calc((100% / 7) * 4); }
      .calendar-navigation-axis-x-label:nth-child(5) { left: calc((100% / 7) * 5); }
      .calendar-navigation-axis-x-label:nth-child(6) { left: calc((100% / 7) * 6); }
    }

    &[data-zoom-level="30"][data-days="28"] {
      .calendar-navigation-axis-x-label:nth-child(1) { left: calc((100% / 28) * 4); }
      .calendar-navigation-axis-x-label:nth-child(2) { left: calc((100% / 28) * 8); }
      .calendar-navigation-axis-x-label:nth-child(3) { left: calc((100% / 28) * 12); }
      .calendar-navigation-axis-x-label:nth-child(4) { left: calc((100% / 28) * 16); }
      .calendar-navigation-axis-x-label:nth-child(5) { left: calc((100% / 28) * 20); }
      .calendar-navigation-axis-x-label:nth-child(6) { left: calc((100% / 28) * 24); }
      .calendar-navigation-axis-x-label:nth-child(7) { display: none; }
    }

    &[data-zoom-level="30"][data-days="29"] {
      .calendar-navigation-axis-x-label:nth-child(1) { left: calc((100% / 29) * 4); }
      .calendar-navigation-axis-x-label:nth-child(2) { left: calc((100% / 29) * 8); }
      .calendar-navigation-axis-x-label:nth-child(3) { left: calc((100% / 29) * 12); }
      .calendar-navigation-axis-x-label:nth-child(4) { left: calc((100% / 29) * 16); }
      .calendar-navigation-axis-x-label:nth-child(5) { left: calc((100% / 29) * 20); }
      .calendar-navigation-axis-x-label:nth-child(6) { left: calc((100% / 29) * 24); }
      .calendar-navigation-axis-x-label:nth-child(7) { display: none; }
    }

    &[data-zoom-level="30"][data-days="30"] {
      .calendar-navigation-axis-x-label:nth-child(1) { left: calc((100% / 30) * 4); }
      .calendar-navigation-axis-x-label:nth-child(2) { left: calc((100% / 30) * 8); }
      .calendar-navigation-axis-x-label:nth-child(3) { left: calc((100% / 30) * 12); }
      .calendar-navigation-axis-x-label:nth-child(4) { left: calc((100% / 30) * 16); }
      .calendar-navigation-axis-x-label:nth-child(5) { left: calc((100% / 30) * 20); }
      .calendar-navigation-axis-x-label:nth-child(6) { left: calc((100% / 30) * 24); }
      .calendar-navigation-axis-x-label:nth-child(7) { left: calc((100% / 30) * 28); }
    }

    &[data-zoom-level="30"][data-days="31"] {
      .calendar-navigation-axis-x-label:nth-child(1) { left: calc((100% / 31) * 4); }
      .calendar-navigation-axis-x-label:nth-child(2) { left: calc((100% / 31) * 8); }
      .calendar-navigation-axis-x-label:nth-child(3) { left: calc((100% / 31) * 12); }
      .calendar-navigation-axis-x-label:nth-child(4) { left: calc((100% / 31) * 16); }
      .calendar-navigation-axis-x-label:nth-child(5) { left: calc((100% / 31) * 20); }
      .calendar-navigation-axis-x-label:nth-child(6) { left: calc((100% / 31) * 24); }
      .calendar-navigation-axis-x-label:nth-child(7) { left: calc((100% / 31) * 28); }
    }

    &[data-zoom-level="60"][data-days="59"] {
      .calendar-navigation-axis-x-label:nth-child(1) { left: calc((100% / 59) * 8); }
      .calendar-navigation-axis-x-label:nth-child(2) { left: calc((100% / 59) * 16); }
      .calendar-navigation-axis-x-label:nth-child(3) { left: calc((100% / 59) * 24); }
      .calendar-navigation-axis-x-label:nth-child(4) { left: calc((100% / 59) * 32); }
      .calendar-navigation-axis-x-label:nth-child(5) { left: calc((100% / 59) * 40); }
      .calendar-navigation-axis-x-label:nth-child(6) { left: calc((100% / 59) * 48); }
      .calendar-navigation-axis-x-label:nth-child(7) { left: calc((100% / 59) * 56); }
    }

    &[data-zoom-level="60"][data-days="60"] {
      .calendar-navigation-axis-x-label:nth-child(1) { left: calc((100% / 60) * 8); }
      .calendar-navigation-axis-x-label:nth-child(2) { left: calc((100% / 60) * 16); }
      .calendar-navigation-axis-x-label:nth-child(3) { left: calc((100% / 60) * 24); }
      .calendar-navigation-axis-x-label:nth-child(4) { left: calc((100% / 60) * 32); }
      .calendar-navigation-axis-x-label:nth-child(5) { left: calc((100% / 60) * 40); }
      .calendar-navigation-axis-x-label:nth-child(6) { left: calc((100% / 60) * 48); }
      .calendar-navigation-axis-x-label:nth-child(7) { left: calc((100% / 60) * 56); }
    }

    &[data-zoom-level="60"][data-days="61"] {
      .calendar-navigation-axis-x-label:nth-child(1) { left: calc((100% / 61) * 8); }
      .calendar-navigation-axis-x-label:nth-child(2) { left: calc((100% / 61) * 16); }
      .calendar-navigation-axis-x-label:nth-child(3) { left: calc((100% / 61) * 24); }
      .calendar-navigation-axis-x-label:nth-child(4) { left: calc((100% / 61) * 32); }
      .calendar-navigation-axis-x-label:nth-child(5) { left: calc((100% / 61) * 40); }
      .calendar-navigation-axis-x-label:nth-child(6) { left: calc((100% / 61) * 48); }
      .calendar-navigation-axis-x-label:nth-child(7) { left: calc((100% / 61) * 56); }
    }

    &[data-zoom-level="60"][data-days="62"] {
      .calendar-navigation-axis-x-label:nth-child(1) { left: calc((100% / 62) * 8); }
      .calendar-navigation-axis-x-label:nth-child(2) { left: calc((100% / 62) * 16); }
      .calendar-navigation-axis-x-label:nth-child(3) { left: calc((100% / 62) * 24); }
      .calendar-navigation-axis-x-label:nth-child(4) { left: calc((100% / 62) * 32); }
      .calendar-navigation-axis-x-label:nth-child(5) { left: calc((100% / 62) * 40); }
      .calendar-navigation-axis-x-label:nth-child(6) { left: calc((100% / 62) * 48); }
      .calendar-navigation-axis-x-label:nth-child(7) { left: calc((100% / 62) * 56); }
    }
  }
}
</style>
